import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useParams, useNavigate} from 'react-router-dom';
import {PortableText} from '@portabletext/react';
import {client, urlFor} from '../../../lib/sanityClient';
import {POST_BY_SLUG_QUERY, RECENT_POSTS_QUERY} from '../../../lib/sanityQueries';

const FALLBACK_IMGS = [
  '/light/assets/imgs/spob/spob-1.jpg',
  '/light/assets/imgs/spob/spob-2.jpg',
  '/light/assets/imgs/spob/spob-3.jpg',
  '/light/assets/imgs/spob/spob-4.jpg',
  '/light/assets/imgs/spob/spob-5.jpg',
  '/light/assets/imgs/spob/spob-6.jpg',
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

const portableTextComponents = {
  types: {
    image: ({value}) => (
      <div className="post-img my-4">
        <img
          src={urlFor(value).width(900).url()}
          alt={value.alt || ''}
          style={{width: '100%', borderRadius: '4px'}}
        />
        {value.caption && (
          <p className="text-center fz-13 opacity-7 mt-10">{value.caption}</p>
        )}
      </div>
    ),
  },
  block: {
    h2: ({children}) => <h2 className="mt-30 mb-15">{children}</h2>,
    h3: ({children}) => <h3 className="mt-25 mb-10">{children}</h3>,
    h4: ({children}) => <h4 className="mt-20 mb-10">{children}</h4>,
    blockquote: ({children}) => (
      <div className="post-qoute mt-40 mb-40">
        <h6 className="fz-20">
          <span className="l-block">{children}</span>
        </h6>
      </div>
    ),
    normal: ({children}) => (
      <div className="text mb-20">
        <p>{children}</p>
      </div>
    ),
  },
  marks: {
    strong: ({children}) => <strong>{children}</strong>,
    em: ({children}) => <em>{children}</em>,
    link: ({value, children}) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
  list: {
    bullet: ({children}) => <ul className="mb-20" style={{paddingLeft: '20px'}}>{children}</ul>,
    number: ({children}) => <ol className="mb-20" style={{paddingLeft: '20px'}}>{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li className="mb-5">{children}</li>,
    number: ({children}) => <li className="mb-5">{children}</li>,
  },
};

function PostDetail() {
  const {slug} = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      client.fetch(POST_BY_SLUG_QUERY, {slug}),
      client.fetch(RECENT_POSTS_QUERY),
    ]).then(([postData, recent]) => {
      if (!postData) {
        navigate('/light/blog-classic');
        return;
      }
      setPost(postData);
      setRecentPosts(recent.filter(p => p.slug.current !== slug));
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, [slug, navigate]);

  if (loading) {
    return (
      <section className="blog section-padding">
        <div className="container">
          <div className="text-center py-5">
            <p className="opacity-7">Loading post...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!post) return null;

  const postUrl = `https://spob-nigeria.vercel.app/light/blog/${post.slug?.current}`;
  const ogImage = post.mainImage
    ? urlFor(post.mainImage).width(1200).height(630).url()
    : `https://spob-nigeria.vercel.app${FALLBACK_IMGS[0]}`;

  return (
    <>
    <Helmet>
      <title>{post.title} | SPOB Nigeria</title>
      <meta name="description" content={post.excerpt || `Read about ${post.title} on the SPOB Nigeria blog.`} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={postUrl} />
      <meta property="og:title" content={`${post.title} | SPOB Nigeria`} />
      <meta property="og:description" content={post.excerpt || `Read about ${post.title} on the SPOB Nigeria blog.`} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${post.title} | SPOB Nigeria`} />
      <meta name="twitter:description" content={post.excerpt || `Read about ${post.title} on the SPOB Nigeria blog.`} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
    <section className="blog section-padding">
      <div className="container">
        <div className="row xlg-marg">

          {/* Post Content */}
          <div className="col-lg-8">
            <div className="main-post">
              <div className="item pb-60">

                {/* Featured Image */}
                {post.mainImage && (
                  <div className="post-img mb-40">
                    <img
                      src={urlFor(post.mainImage).width(900).url()}
                      alt={post.mainImage.alt || post.title}
                      style={{width: '100%', borderRadius: '4px'}}
                    />
                  </div>
                )}

                {/* Meta */}
                <div className="d-flex align-items-center mb-20 opacity-7 fz-13">
                  <span className="mr-20">{formatDate(post.publishedAt)}</span>
                  {post.author && (
                    <span className="mr-20">By {post.author.name}</span>
                  )}
                  {post.categories && post.categories.length > 0 && (
                    <span>{post.categories.map(c => c.title).join(', ')}</span>
                  )}
                </div>

                <h2 className="mb-30">{post.title}</h2>

                {post.excerpt && (
                  <div className="text mb-30">
                    <p className="fz-18 fw-300">{post.excerpt}</p>
                  </div>
                )}

                {/* Rich Text Body */}
                {post.body && (
                  <article>
                    <PortableText value={post.body} components={portableTextComponents} />
                  </article>
                )}

                {/* Author Bio */}
                {post.author && post.author.bio && (
                  <div className="author mt-60 pt-40" style={{borderTop: '1px solid #e8e8e8'}}>
                    <div className="d-flex align-items-center">
                      {post.author.image && (
                        <div className="mr-20">
                          <img
                            src={urlFor(post.author.image).width(80).height(80).url()}
                            alt={post.author.name}
                            style={{borderRadius: '50%', width: '80px', height: '80px', objectFit: 'cover'}}
                          />
                        </div>
                      )}
                      <div>
                        <h6 className="mb-5">{post.author.name}</h6>
                        <p className="fz-13 opacity-7">{post.author.bio}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Back Link */}
                <div className="mt-50">
                  <a href="/light/blog-classic" className="d-flex align-items-center">
                    <span className="ti-arrow-top-left mr-10"></span>
                    <span className="text">Back to Blog</span>
                  </a>
                </div>

              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            <div className="sidebar">
              <div className="widget tags">
                <h6 className="title-widget">Tags</h6>
                <div>
                  <a href="/light/blog-classic">Health</a>
                  <a href="/light/blog-classic">Education</a>
                  <a href="/light/blog-classic">Culture</a>
                  <a href="/light/blog-classic">Environment</a>
                  <a href="/light/blog-classic">Outreach</a>
                  <a href="/light/blog-classic">Youth</a>
                </div>
              </div>

              {recentPosts.length > 0 && (
                <div className="widget last-post-thum">
                  <h6 className="title-widget">More Posts</h6>
                  {recentPosts.slice(0, 4).map((rp, i) => (
                    <div className="item d-flex align-items-center" key={rp._id}>
                      <div>
                        <div className="img">
                          <a href={`/light/blog/${rp.slug.current}`}>
                            <img
                              src={
                                rp.mainImage
                                  ? urlFor(rp.mainImage).width(120).height(100).url()
                                  : FALLBACK_IMGS[i % FALLBACK_IMGS.length]
                              }
                              alt={rp.title}
                            />
                            <span className="date">
                              <span>
                                {new Date(rp.publishedAt).getDate()} /{' '}
                                <br />
                                {new Date(rp.publishedAt).toLocaleString('en', {month: 'short'})}
                              </span>
                            </span>
                          </a>
                        </div>
                      </div>
                      <div className="cont">
                        <h6>
                          <a href={`/light/blog/${rp.slug.current}`}>{rp.title}</a>
                        </h6>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
    </>
  );
}

export default PostDetail;
